// DSGVO Cookie Consent Management
// Manages user consent for tracking categories

export type ConsentCategory = 'necessary' | 'statistics' | 'marketing';

export interface ConsentState {
  necessary: boolean;   // Always true
  statistics: boolean;  // Google Analytics
  marketing: boolean;   // Meta Pixel + Google Ads
  timestamp: string;
  // Per-service granularity (optional, defaults to category value)
  services?: {
    ga4?: boolean;
    metaPixel?: boolean;
    googleAds?: boolean;
    affiliate?: boolean;
  };
}

const CONSENT_KEY = 'h2h_cookie_consent';

export function getConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.statistics !== 'boolean' || typeof parsed.marketing !== 'boolean') return null;
    return parsed as ConsentState;
  } catch {
    return null;
  }
}

export function saveConsent(state: ConsentState): void {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
  window.dispatchEvent(new Event('h2h-consent-change'));
}

export function hasConsent(): boolean {
  return getConsent() !== null;
}

export function acceptAll(): ConsentState {
  const state: ConsentState = {
    necessary: true,
    statistics: true,
    marketing: true,
    timestamp: new Date().toISOString(),
    services: { ga4: true, metaPixel: true, googleAds: true, affiliate: true },
  };
  saveConsent(state);
  return state;
}

export function acceptNecessaryOnly(): ConsentState {
  const state: ConsentState = {
    necessary: true,
    statistics: false,
    marketing: false,
    timestamp: new Date().toISOString(),
    services: { ga4: false, metaPixel: false, googleAds: false, affiliate: false },
  };
  saveConsent(state);
  return state;
}

export function saveCustomConsent(
  statistics: boolean,
  marketing: boolean,
  services?: { ga4?: boolean; metaPixel?: boolean; googleAds?: boolean; affiliate?: boolean }
): ConsentState {
  const state: ConsentState = {
    necessary: true,
    statistics,
    marketing,
    timestamp: new Date().toISOString(),
    services: services ?? {
      ga4: statistics,
      metaPixel: marketing,
      googleAds: marketing,
    },
  };
  saveConsent(state);
  return state;
}

/** Helper: check if a specific service is consented */
export function hasServiceConsent(serviceId: 'ga4' | 'metaPixel' | 'googleAds' | 'affiliate'): boolean {
  const consent = getConsent();
  if (!consent) return false;
  if (consent.services) return consent.services[serviceId] === true;
  // Fallback to category-level
  if (serviceId === 'ga4') return consent.statistics === true;
  return consent.marketing === true;
}
